import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, ArrowRight, CheckCircle2, Lock, Save, Send } from "lucide-react";
import { toast } from "sonner";
import { useStrategy } from "@/context/StrategyContext";
import { sapi, errText } from "@/lib/strategy";
import QuestionRenderer, { isComplete } from "@/components/strategy/QuestionRenderer";
import { Loading, Panel, PageHead, DemoBadge, ErrorNote } from "@/components/strategy/ui";

export default function Assessment() {
  const { questions, round, roundId, refreshRounds, me } = useStrategy();
  const navigate = useNavigate();
  const [resp, setResp] = useState(null);
  const [answers, setAnswers] = useState({});
  const [idx, setIdx] = useState(0);
  const [saving, setSaving] = useState("idle"); // idle | saving | saved | error
  const [review, setReview] = useState(false);
  const [error, setError] = useState("");
  const dirty = useRef({});
  const timer = useRef(null);

  const qs = questions?.questions || [];
  const sections = questions?.sections || [];

  useEffect(() => {
    if (!roundId || !qs.length) return;
    setResp(null);
    sapi.get(`/rounds/${roundId}/response`).then((r) => {
      setResp(r.data);
      setAnswers(r.data.answers || {});
      setIdx(Math.min(r.data.current_index || 0, Math.max(0, qs.length - 1)));
    }).catch((e) => setError(errText(e)));
  }, [roundId, qs.length]);

  const flush = useCallback(async (nextIdx) => {
    const payload = { ...dirty.current };
    dirty.current = {};
    if (!Object.keys(payload).length && (nextIdx == null || resp?.status === "not_started")) return;
    setSaving("saving");
    try {
      const { data } = await sapi.put(`/rounds/${roundId}/response`, { answers: payload, current_index: nextIdx ?? idx });
      setResp(data);
      setSaving("saved");
    } catch (e) {
      setSaving("error");
      toast.error(errText(e));
    }
  }, [roundId, idx, resp?.status]);

  const schedule = useCallback(() => {
    clearTimeout(timer.current);
    timer.current = setTimeout(() => flush(), 700);
  }, [flush]);

  const update = (qid, patch) => {
    setAnswers((a) => {
      const cur = a[qid] || { value: null, reason: "" };
      const next = { ...cur, ...patch };
      dirty.current[qid] = next;
      return { ...a, [qid]: next };
    });
    schedule();
  };

  const go = async (n) => {
    clearTimeout(timer.current);
    const target = Math.max(0, Math.min(qs.length, n));
    await flush(target >= qs.length ? qs.length - 1 : target);
    if (target >= qs.length) setReview(true); else { setReview(false); setIdx(target); }
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const completed = useMemo(() => qs.filter((q) => isComplete(q, answers[q.id]?.value)).length, [qs, answers]);
  const missing = useMemo(() => qs.filter((q) => !isComplete(q, answers[q.id]?.value)), [qs, answers]);

  const submit = async () => {
    await flush();
    try {
      await sapi.post(`/rounds/${roundId}/submit`);
      toast.success("Assessment submitted — thank you");
      await refreshRounds();
      setResp((r) => ({ ...r, status: "submitted" }));
    } catch (e) { toast.error(errText(e)); }
  };

  if (!questions || !round) return <Loading label="Loading your assessment" />;
  if (error) return <ErrorNote error={error} />;
  if (!resp) return <Loading label="Loading your answers" />;

  if (round.demo) {
    return (
      <>
        <PageHead eyebrow="My assessment" title="Demo round is read-only" sub="This round contains seeded demonstration answers so the dashboards can be explored. Switch to a live round to complete your own assessment." actions={<DemoBadge />} />
        <Panel className="p-8 text-center"><button className="st-btn st-btn-primary" onClick={() => navigate("/roadmap/admin")} data-testid="assess-goto-admin">Choose a live round</button></Panel>
      </>
    );
  }

  if (resp.status === "submitted") {
    return (
      <>
        <PageHead eyebrow="My assessment" title="Submitted" sub="Your answers are locked and private. Results unlock once all three founders have submitted." />
        <Panel className="p-10 text-center st-fade-in" data-testid="assess-submitted">
          <span className="grid place-items-center w-14 h-14 rounded-2xl mx-auto mb-5" style={{ background: "var(--st-lime-dim)" }}><Lock size={24} style={{ color: "var(--st-lime)" }} /></span>
          <h3 className="text-xl font-extrabold">Thank you, {me.name}</h3>
          <p className="st-muted text-sm mt-2 max-w-md mx-auto leading-relaxed">To change an answer, ask an administrator to reopen your assessment from Rounds &amp; admin.</p>
          <div className="flex justify-center gap-2 mt-6">
            <button className="st-btn st-btn-ghost" onClick={() => navigate("/roadmap")} data-testid="assess-home">Back to home</button>
            <button className="st-btn st-btn-primary" onClick={() => navigate("/roadmap/alignment")} data-testid="assess-view-alignment">View alignment</button>
          </div>
        </Panel>
      </>
    );
  }

  const q = qs[idx];
  const section = sections.find((s) => s.id === q.section);
  const sIdx = sections.findIndex((s) => s.id === q.section);
  const inSection = qs.filter((x) => x.section === q.section);
  const posInSection = inSection.findIndex((x) => x.id === q.id) + 1;
  const a = answers[q.id] || { value: null, reason: "" };
  const done = isComplete(q, a.value);

  if (review) {
    return (
      <div className="max-w-3xl mx-auto st-fade-in">
        <PageHead eyebrow="Review & submit" title={missing.length ? `${missing.length} question${missing.length > 1 ? "s" : ""} still open` : "Everything is answered"}
          sub={missing.length ? "Jump to any open question below. You can submit once every question is complete." : "Submitting locks your answers. They stay private until all three founders have submitted."} />
        <Panel className="p-6 space-y-3">
          {missing.map((m) => (
            <button key={m.id} onClick={() => { setReview(false); setIdx(qs.indexOf(m)); }} className="st-option" data-testid={`review-missing-${m.id}`}>
              <span className="st-pill" style={{ background: "rgba(240,181,92,.14)", color: "var(--st-amber)" }}>{m.id}</span>
              <span className="font-semibold text-sm">{m.title}</span>
            </button>
          ))}
          {!missing.length && <div className="flex items-center gap-3 st-muted text-sm"><CheckCircle2 size={18} style={{ color: "var(--st-lime)" }} /> 48 of 48 complete</div>}
          <div className="flex justify-between pt-4">
            <button className="st-btn st-btn-ghost" onClick={() => { setReview(false); }} data-testid="review-back"><ArrowLeft size={16} /> Back to questions</button>
            <button className="st-btn st-btn-primary" disabled={!!missing.length} onClick={submit} data-testid="assess-submit"><Send size={16} /> Submit assessment</button>
          </div>
        </Panel>
      </div>
    );
  }

  return (
    <div className="max-w-3xl mx-auto">
      <div className="flex items-center justify-between mb-5 gap-4">
        <div className="flex items-center gap-2 flex-wrap">
          {sections.map((s, i) => (
            <button key={s.id} onClick={() => go(qs.findIndex((x) => x.section === s.id))} data-testid={`assess-section-${s.id}`}
              className="st-pill transition-all" style={{ background: i === sIdx ? "var(--st-lime-dim)" : "rgba(243,239,230,.05)", color: i === sIdx ? "var(--st-lime)" : "var(--st-faint)" }}>{s.id} · {s.short}</button>
          ))}
        </div>
        <span className="st-faint text-xs flex items-center gap-1.5 shrink-0" data-testid="assess-save-state">
          <Save size={12} /> {saving === "saving" ? "Saving…" : saving === "error" ? "Save failed" : "Autosaved"}
        </span>
      </div>
      <div className="st-bar mb-8"><span style={{ width: `${(completed / qs.length) * 100}%` }} /></div>

      <AnimatePresence mode="wait">
        <motion.div key={q.id} initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -10 }} transition={{ duration: 0.3, ease: [0.16, 1, 0.3, 1] }}>
          <p className="st-caps mb-2" data-testid="assess-progress">{section?.name} · Question {posInSection} of {inSection.length} · {idx + 1} / {qs.length} overall · {completed} answered</p>
          <h2 className="text-2xl sm:text-3xl font-extrabold tracking-tight leading-tight" data-testid="assess-question-title">{q.title}</h2>
          {q.scenario && <Panel className="mt-4 p-5"><p className="st-caps mb-1">Scenario</p><p className="text-[15px] leading-relaxed">{q.scenario}</p></Panel>}
          <p className="st-muted text-sm mt-3 leading-relaxed"><span className="font-semibold" style={{ color: "var(--st-lime-2)" }}>Why this matters · </span>{q.why}</p>

          <div className="mt-7"><QuestionRenderer q={q} value={a.value} onChange={(v) => update(q.id, { value: v })} /></div>

          <div className="mt-7">
            <label className="st-caps block mb-2">Why do you think this? <span className="normal-case tracking-normal font-medium st-faint">(optional)</span></label>
            <textarea value={a.reason || ""} onChange={(e) => update(q.id, { reason: e.target.value.slice(0, 1000) })} placeholder="A sentence or two of reasoning helps the other founders understand your position." className="st-textarea" data-testid={`q-${q.id}-reason`} />
          </div>
        </motion.div>
      </AnimatePresence>

      <div className="flex items-center justify-between mt-8 pt-6 border-t" style={{ borderColor: "var(--st-line)" }}>
        <button className="st-btn st-btn-ghost" onClick={() => go(idx - 1)} disabled={idx === 0} data-testid="assess-prev"><ArrowLeft size={16} /> Previous</button>
        <span className="st-faint text-xs hidden sm:block">{done ? "Answered" : "Unanswered — you can come back later"}</span>
        <button className={`st-btn ${done ? "st-btn-primary" : "st-btn-ghost"}`} onClick={() => go(idx + 1)} data-testid="assess-next">{idx === qs.length - 1 ? "Review & submit" : "Next"} <ArrowRight size={16} /></button>
      </div>
    </div>
  );
}
